/* eslint-disable @typescript-eslint/no-non-null-assertion */
import type { PrimaryPageAction } from "@octopusdeploy/design-system-components";
import type { SubscriptionResource, ResourceCollection, GetTeamsLookupBffResponseTeam } from "@octopusdeploy/octopus-server-client";
import { Permission } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import type { Dictionary } from "lodash";
import { keyBy } from "lodash";
import * as React from "react";
import { repository } from "~/clientInstance";
import { TeamChip, DisabledChip } from "~/components/Chips/index";
import { DataBaseComponent } from "~/components/DataBaseComponent";
import type { DataBaseComponentState } from "~/components/DataBaseComponent";
import { OverflowMenuItems } from "~/components/OverflowMenu/OverflowMenu";
import { PageContent } from "~/components/PageContent/PageContent";
import List from "~/components/PagingList";
import { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import ListItem from "~/primitiveComponents/dataDisplay/ListItem/ListItem";
import ListTitle from "~/primitiveComponents/dataDisplay/ListTitle/ListTitle";
import { timeOperationOptions } from "~/utils/OperationTimer/timeOperation";
import TimeSpanHelper from "~/utils/TimeSpanHelper";
import styles from "./style.module.less";
class SubscriptionList extends List<SubscriptionResource> {
}
interface SubscriptionPageState extends DataBaseComponentState {
    subscriptionsResponse: ResourceCollection<SubscriptionResource>;
    teamIndex: Dictionary<GetTeamsLookupBffResponseTeam>;
}
export class SubscriptionsPage extends DataBaseComponent<{}, SubscriptionPageState> {
    constructor(props: {}) {
        super(props);
        this.state = {
            subscriptionsResponse: null!,
            teamIndex: null!,
        };
    }
    async componentDidMount() {
        await this.doBusyTask(async () => {
            const subscriptionsResponse = await repository.Subscriptions.list();
            const teams = await repository.Teams.lookupBff();
            const teamIndex = keyBy(teams, (t) => t.Id);
            this.setState({ subscriptionsResponse, teamIndex });
        }, { timeOperationOptions: timeOperationOptions.forInitialLoad() });
    }
    render() {
        const addSubscriptionPageAction: PrimaryPageAction = {
            type: "navigate",
            hasPermissions: isAllowed({ permission: Permission.SubscriptionCreate }),
            label: "Add Subscription",
            path: links.createSubscriptionPage.generateUrl(),
        };
        return (<PageContent busy={this.state.busy} errors={this.errors} header={{ title: "Subscriptions", primaryAction: addSubscriptionPageAction }}>
                {this.state.subscriptionsResponse && (<SubscriptionList initialData={this.state.subscriptionsResponse} onRow={(item: SubscriptionResource) => this.buildSubscriptionRow(item)} onFilter={this.filter} filterSearchEnabled={true} apiSearchParams={["partialName"]} onRowRedirectUrl={(item: SubscriptionResource) => links.editSubscriptionPage.generateUrl({ subscriptionId: item.Id })} filterHintText="Filter by name..."/>)}
            </PageContent>);
    }
    private filter(filter: string, resource: SubscriptionResource) {
        return !filter || filter.length === 0 || !resource || resource.Name.toLowerCase().includes(filter.toLowerCase());
    }
    private handleEnabledToggle = async (model: SubscriptionResource) => {
        model.IsDisabled = !model.IsDisabled; // Toggle the current value.
        await this.doBusyTask(async () => {
            const result = await repository.Subscriptions.save(model);
            const response: ResourceCollection<SubscriptionResource> = this.state.subscriptionsResponse;
            response.Items.forEach((item: SubscriptionResource, i: number) => {
                if (item.Id === result.Id) {
                    response.Items[i] = result;
                }
            });
            this.setState({
                subscriptionsResponse: response,
            });
        });
    };
    private buildSubscriptionRow(subscription: SubscriptionResource) {
        const emailTeams = subscription.EventNotificationSubscription.EmailTeams.map((id) => this.state.teamIndex[id]).filter((t) => !!t);
        const emailFrequencyPeriodText = TimeSpanHelper.timeSpanTextToHumanReadableString(subscription.EventNotificationSubscription.EmailFrequencyPeriod);
        const webhookTeams = subscription.EventNotificationSubscription.WebhookTeams.map((id) => this.state.teamIndex[id]).filter((t) => !!t);
        return (<div className={subscription.IsDisabled ? styles.disabled : null}>
                <ListItem overflowMenuItems={[OverflowMenuItems.item(subscription.IsDisabled ? "Enable" : "Disable", () => this.handleEnabledToggle(subscription), { permission: Permission.SubscriptionEdit })]}>
                    <ListTitle>
                        {subscription.Name}
                        {subscription.IsDisabled && (<span>
                                {" "}
                                <DisabledChip />
                            </span>)}
                    </ListTitle>

                    {emailTeams.length > 0 && (<span>
                            Email the teams{" "}
                            {emailTeams.map((t) => (<TeamChip key={t.Id} team={t}/>))}{" "}
                            every {emailFrequencyPeriodText}
                        </span>)}
                    {emailTeams.length > 0 && subscription.EventNotificationSubscription.WebhookURI && <br />}
                    {subscription.EventNotificationSubscription.WebhookURI && (<span>
                            Send a POST to webhook <code>{subscription.EventNotificationSubscription.WebhookURI}</code>
                            {webhookTeams.length > 0 && (<span>
                                    {" "}
                                    with events scoped to those viewable by teams:{" "}
                                    {webhookTeams.map((t) => (<TeamChip key={t.Id} team={t}/>))}
                                </span>)}
                        </span>)}
                </ListItem>
            </div>);
    }
    static displayName = "SubscriptionsPage";
}
